import { Alert, StyleSheet, View } from "react-native";
import { FIREBASE_EVENT_NAMES, FIREBASE_EVENT_PARAMS, PAGES_ROUTES, PAGES_TITLE } from "utils/Constants";
import React, { useEffect, useRef } from "react";
import Svg, { ClipPath, Defs, G, Path, Rect } from "react-native-svg";
import { executeAuthorizeNet, placeOrder } from "appRedux/reducers/orderSlice";
import { fp, hp, spH, spV, wp } from "utils/normalise";
import { router, useLocalSearchParams } from "expo-router";
import { useDispatch, useSelector } from "react-redux";

import { ANALYTICS_EVENT_NAMES } from "utils/analytics";
import Buttons from "components/Buttons";
import Input from "components/Input";
import Loader from "components/Loader";
import ParentScrollView from "components/ParentScrollView";
import { TouchableOpacity } from "react-native";
import VText from "components/VText";
import { analytics } from "utils/firebaseConfig";
import { getCart } from "appRedux/reducers/cartSlice";
import { logEvent } from "firebase/analytics";
import { saveUserCard } from "appRedux/reducers/profileSlice";
import { trackV2 } from "utils/Segment";
import { useState } from "react";

const Payment = ({ selectedCard, setSelectedCard }) => {
    const [loader, setLoader] = useState(false);
    const [name, setName] = useState("");
    const [cardNumber, setCardNumber] = useState("");
    const [expirationDate, setExpirationDate] = useState("");
    const [securityCode, setSecurityCode] = useState("");
    const [ccType, setCcType] = useState(null);
    const [zipCode, setZipCode] = useState("");
    const [saveCardChecked, setSaveCardChecked] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        address: "",
        cardNumber: "",
        securityCode: "",
        expiryDate: "",
        cvv: "",
        zipCode: "",
        generalError: "",
    });

    const deliveryAddress = useSelector((state) => state.OrderReducer.deliveryAddress);
    const userId = useSelector((state) => state.AuthReducer.userId);
    const userProfile = useSelector((state) => state.ProfileReducer.profileData);
    const cartDataItems = useSelector((state) => state.CartReducer.cart);
    const orderSummary = useSelector((state) => state.OrderReducer.orderSummary);
    const orderCoupon = useSelector((state) => state.OrderReducer.orderCoupon);
    const billingAddress = useSelector((state) => state.OrderReducer.billingAddress);

    const dispatch = useDispatch();
    const local = useLocalSearchParams();
    const gender = local?.gender || "female";

    useEffect(() => {
        logEvent(analytics, FIREBASE_EVENT_NAMES.PAGE_VIEW, {
            [FIREBASE_EVENT_PARAMS.PAGE_TITLE]: PAGES_TITLE.PAYMENT,
            [FIREBASE_EVENT_PARAMS.PAGE_LOCATION]: PAGES_ROUTES.PAYMENT,
        });
    }, []);

    useEffect(() => {
        const script = document?.createElement("script");
        script.src = "https://js.authorize.net/v1/Accept.js";
        script.async = true;

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const FloatingLabelInput = ({ label, value, onChange, placeholder, maxLength, isError, message, regex }) => {
        return (
            <div style={{ position: "relative", marginBottom: "20px", width: "-webkit-fill-available" }}>
                <input
                    type="text"
                    value={value}
                    className={`floating-label-input ${isError ? "input-error" : ""}`}
                    onChange={onChange}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    pattern={regex}
                    style={{
                        width: "-webkit-fill-available",
                        padding: 16,
                        border: `1px solid ${isError ? "#E20E0E" : "#21242799"}`,
                        outline: "none",
                        borderRadius: 4,
                        fontFamily: "JosefinSans",
                        fontSize: fp(16),
                        color: isError ? "#E20E0E" : "#999",
                        backgroundColor: "white",
                    }}
                />
                <label
                    style={{
                        position: "absolute",
                        paddingLeft: 10,
                        paddingRight: 10,
                        top: "-6px",
                        left: "15px",
                        fontFamily: "JosefinSans",
                        fontSize: fp(16),
                        color: isError ? "#E20E0E" : "#999",
                        transition: "0.2s ease all",
                        pointerEvents: "none",
                        backgroundColor: "white",
                    }}
                >
                    {label}
                </label>
                {message && (
                    <VText style={{ color: "#E20E0E", fontSize: fp(14), fontFamily: "JosefinSans", marginTop: spV(5) }}>
                        {message}
                    </VText>
                )}
            </div>
        );
    };

    const detectCardType = (number) => {
        const cardTypes = [
            {
                regex: /^3[47]\d{0,13}$/,
                cardType: "american express",
                mask: "0000 000000 00000",
            },
            {
                regex: /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}$/,
                cardType: "discover",
                mask: "0000 0000 0000 0000",
            },
            {
                regex: /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}$/,
                cardType: "diners",
                mask: "0000 000000 0000",
            },
            {
                regex: /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}$/,
                cardType: "mastercard",
                mask: "0000 0000 0000 0000",
            },
            {
                regex: /^(?:2131|1800)\d{0,11}$/,
                cardType: "jcb15",
                mask: "0000 000000 00000",
            },
            {
                regex: /^(?:35\d{0,2})\d{0,12}$/,
                cardType: "jcb",
                mask: "0000 0000 0000 0000",
            },
            {
                regex: /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}$/,
                cardType: "maestro",
                mask: "0000 0000 0000 0000",
            },
            { regex: /^4\d{0,15}$/, cardType: "visa", mask: "0000 0000 0000 0000" },
            {
                regex: /^62\d{0,14}$/,
                cardType: "unionpay",
                mask: "0000 0000 0000 0000",
            },
        ];

        for (let i = 0; i < cardTypes.length; i++) {
            if (number.match(cardTypes[i].regex)) {
                return cardTypes[i];
            }
        }

        // Default case if no card type matches
        return { cardType: "Unknown", mask: "0000 0000 0000 0000 000" };
    };

    const applyMask = (number, mask) => {
        let cleaned = number.replace(/\D+/g, ""); // Remove non-numeric characters
        let formatted = "";

        let maskIndex = 0;
        for (let i = 0; i < cleaned.length; i++) {
            // Ensure we don't exceed the mask's length
            if (maskIndex >= mask.length) {
                break;
            }

            // If the mask character is a "0", add the number character
            if (mask[maskIndex] === "0") {
                formatted += cleaned[i];
                maskIndex++;
            }
            // Otherwise, add the mask character (like space) and stay on the same number
            else {
                formatted += mask[maskIndex];
                maskIndex++;
                i--; // Stay on the same character in the cleaned number
            }
        }

        return formatted;
    };

    const handleCardNumberChange = (text) => {
        setCardNumber(text);

        // Logic to detect the card type based on regex patterns
        if (/^4/.test(text)) {
            setCcType("visa");
        } else if (/^5[1-5]/.test(text)) {
            setCcType("mastercard");
        } else if (/^3[47]/.test(text)) {
            setCcType("amex");
        } else {
            setCcType("unknown");
        }
    };

    const handlePayment = () => {
        setErrorMessage({});
        const eventData = {
            payment_method: selectedCard ? "saved_card" : "new_card",
            saved_card_used: selectedCard ? true : false,
            promo_code: orderCoupon?.code || "",
            user_status: userId ? "logged_in" : "guest",
            bag_total_value: orderSummary?.payableAmount - (orderCoupon?.discountAmount || 0),
            promo_discount_applied: orderCoupon?.discountAmount && orderCoupon?.discountAmount > 0 ? true : false,
        };
        trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_INITIATED, eventData);
        let cardNumberWithoutSpaces = cardNumber?.replaceAll(" ", "");
        const validateCardDetails = () => {
            const errors = {};
            if (cardNumberWithoutSpaces.length < 13 || cardNumberWithoutSpaces.length > 19) {
                errors.cardNumber = "Please enter a valid Card Number";
            }

            const expiryParts = expirationDate.split("/");
            const expiryMonth = parseInt(expiryParts[0], 10);
            const expiryYear = parseInt(`20${expiryParts[1]}`, 10);
            const currentDate = new Date();
            const expiryDate = new Date(expiryYear, expiryMonth - 1);

            // Adjust the currentDate to the start of the month for comparison
            const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

            if (expirationDate.length !== 5 || expiryMonth > 12 || expiryDate < currentMonthStart) {
                errors.expiryDate = "Please enter a valid Expiry Date in MM/YY format and ensure it's not a past date";
            }

            if (securityCode.length < 3 || securityCode.length > 4 || !/^\d+$/.test(securityCode)) {
                errors.securityCode = "Please enter a valid Security Code (3-4 digits)";
            }

            if (!zipCode.trim()) {
                errors.zipCode = "Zip Code cannot be empty";
            }

            return errors;
        };

        const errors = validateCardDetails();

        if (selectedCard === null) {
            if (Object.keys(errors).length > 0) {
                setErrorMessage(errors);
                return;
            }
        }

        var authData = {};
        authData.clientKey = process.env.EXPO_PUBLIC_AUTHORIZE_CLIENTID;
        authData.apiLoginID = process.env.EXPO_PUBLIC_AUTHORIZE_API_LOGINID;
        var cardData = {};
        cardData.cardNumber = cardNumberWithoutSpaces;
        let cardDate = expirationDate;
        let parts = cardDate.split("/");
        cardData.month = parts[0];
        cardData.year = parts[1];
        cardData.cardCode = securityCode;
        cardData.zip = zipCode;

        var secureData = {};
        secureData.authData = authData;
        secureData.cardData = cardData;

        setLoader(true);

        Accept.dispatchData(secureData, function (response) {
            console.log("accept res", response);
            if ((response?.messages?.message && Array.isArray(response?.messages?.message)) || selectedCard) {
                if ((response?.opaqueData?.dataDescriptor && response?.opaqueData?.dataValue) || selectedCard) {
                    setLoader(true);
                    const productData = cartDataItems?.cart
                        ?.filter((o) => {
                            // Filter out items with affiliate brand name and link
                            return !(o?.product?.affiliateBrandName && o?.product?.affiliateLink);
                        })
                        ?.filter((o) => o?.isAvailable)
                        ?.map((item) => {
                            const id = item.product.id;
                            const quantity = item.quantity;
                            const size = item.productSize ? { size: item.productSize } : {};

                            // Find the corresponding product in orderSummary array
                            const matchingProduct = orderSummary?.products?.find((product) => product.productId === id);
                            const selectedShippingMethod = matchingProduct
                                ? matchingProduct.selectedShippingMethod?.name
                                : undefined;

                            return {
                                id,
                                quantity,
                                ...size,
                                selectedShippingMethod, // Include selectedShippingMethod if available
                            };
                        });
                    let data = {
                        coupon: orderCoupon || null,
                        addressId: deliveryAddress?.id,
                        paymentAddressId: billingAddress?.id,
                        paymentMethod: "card",
                        products: productData,
                        totalAmount: orderSummary?.payableAmount - (orderCoupon?.discountAmount || 0),
                        userId: userId,
                    };

                    dispatch(
                        placeOrder({
                            ...data,
                        })
                    )
                        .then((res1) => {
                            console.log("placeOrder res", res1?.payload);
                            if ([200].includes(res1?.payload?.status)) {
                                setLoader(true);
                                const orderLocalId = res1?.payload?.data?.id?.replace(/['"]+/g, "");

                                let paymentPayload = {
                                    userId: userId,
                                    orderId: orderLocalId,
                                };
                                if (selectedCard) {
                                    paymentPayload.customerPaymentProfileId = selectedCard?.customerPaymentProfileId;
                                }
                                if (response?.opaqueData?.dataDescriptor && response?.opaqueData?.dataValue) {
                                    paymentPayload.dataDescriptor = response?.opaqueData?.dataDescriptor;
                                    paymentPayload.dataValue = response?.opaqueData?.dataValue;
                                }

                                // Execute payment
                                dispatch(executeAuthorizeNet(paymentPayload))
                                    .then((res) => {
                                        console.log("executeAuthorizeNet res", res);
                                        setLoader(false);
                                        if (
                                            [200].includes(res?.payload?.status) &&
                                            res?.payload?.data?.message == "success"
                                        ) {
                                            // Save card
                                            if (saveCardChecked) {
                                                const saveCardPayload = {
                                                    userId: userId,
                                                    firstName: billingAddress?.name,
                                                    lastName: "",
                                                    address:
                                                        billingAddress?.addressLine1 +
                                                        ", " +
                                                        billingAddress?.addressLine2,
                                                    city: billingAddress?.city,
                                                    state: billingAddress?.state,
                                                    country: billingAddress.country ? billingAddress.country : "USA",
                                                    phoneNumber: billingAddress?.mobileNumber,
                                                    cardNumber: cardNumber.replace(/\s+/g, ""),
                                                    nameOnCard: name,
                                                    validThru: expirationDate.replace(/(\d{2})$/, "20$1"),
                                                    securityCode: securityCode,
                                                    zipcode: zipCode,
                                                };
                                                dispatch(saveUserCard(saveCardPayload));
                                            }
                                            const eventData = {
                                                bag_total_value:
                                                    orderSummary?.payableAmount - (orderCoupon?.discountAmount || 0),
                                                promo_code: orderCoupon?.code || "",
                                                transaction_id: res?.payload?.data?.order?.paymentTransactionId,
                                                order_id: orderLocalId,
                                                payment_method: selectedCard ? "saved_card" : "new_card",
                                            };
                                            trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_COMPLETED, eventData);

                                            const eventData2 = {
                                                order_id: orderLocalId,
                                                user_status: userId ? "logged_in" : "guest",
                                                transaction_id: res?.payload?.data?.order?.paymentTransactionId,
                                                bag_product_count: res?.payload?.data?.order?.products?.length || 0,
                                                bag_total_value: res?.payload?.data?.order?.totalAmount,
                                                promo_discount: res?.payload?.data?.order?.discountAmount || 0,
                                            };
                                            console.log("eventData", eventData);
                                            trackV2(ANALYTICS_EVENT_NAMES.ORDER_PLACED, eventData2);

                                            router.push(`/order-status?id=${orderLocalId}&gender=${gender}`);
                                            dispatch(getCart());
                                        } else if (typeof res?.payload?.data == "string") {
                                            const eventData = {
                                                payment_method: selectedCard ? "saved_card" : "new_card",
                                                failure_reason: res?.payload?.data,
                                            };
                                            trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                                            alert(res?.payload?.data);
                                        } else {
                                            const eventData = {
                                                payment_method: selectedCard ? "saved_card" : "new_card",
                                                failure_reason: res?.payload?.data?.message,
                                            };
                                            trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                                            alert("Error");
                                        }
                                    })
                                    .catch((err) => {
                                        console.log("err", err);
                                        const eventData = {
                                            payment_method: selectedCard ? "saved_card" : "new_card",
                                            failure_reason: err?.message || JSON.stringify(err),
                                        };
                                        trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                                        alert("Error, Please Try after some time!");
                                        setLoader(false);
                                    });
                            } else {
                                console.log("placeOrder res failed 1");
                                setLoader(false);
                                alert("Error, Please Try after some time!");
                                const eventData = {
                                    payment_method: selectedCard ? "saved_card" : "new_card",
                                    failure_reason: "v1/order/payment API failed",
                                };
                                trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                            }
                        })
                        .catch((err) => {
                            setLoader(false);
                            alert("Error");
                            console.log("err", err);
                            const eventData = {
                                payment_method: selectedCard ? "saved_card" : "new_card",
                                failure_reason: err?.message || JSON.stringify(err),
                            };
                            trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                        });
                } else {
                    console.log("accept res failed 2");
                    alert(response?.messages?.message?.[0]?.text);
                    const eventData = {
                        payment_method: selectedCard ? "saved_card" : "new_card",
                        failure_reason: response?.messages?.message?.[0]?.text,
                    };
                    trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                    setLoader(false);
                }
            } else {
                const eventData = {
                    payment_method: selectedCard ? "saved_card" : "new_card",
                    failure_reason: "accept error",
                };
                trackV2(ANALYTICS_EVENT_NAMES.PAYMENT_FAILED, eventData);
                console.log("accept res failed");
                alert("Error");
                setLoader(false);
            }
        });
    };

    const VisaIcon = () => (
        <Svg width={50} height={50} viewBox="0 0 750 471">
            <G fill="none" fillRule="evenodd">
                <Rect fill="#0E4595" width="750" height="471" rx="40" />
                <Path
                    fill="#FFFFFF"
                    d="M278.1975 334.2275 311.5585 138.4655 364.9175 138.4655 331.5335 334.2275"
                ></Path>
            </G>
        </Svg>
    );

    const MastercardIcon = () => (
        <Svg width={50} height={50} viewBox="0 0 750 471">
            <G fill="none" fillRule="evenodd">
                <Rect fill="#000000" width="750" height="471" rx="40" />
                <G>
                    <Rect fill="#FF5F00" x="170.55" y="32.39" width="143.72" height="234.42" />
                    <Path
                        d="M185.05 149.6 C185.05997 103.912554 205.96046 60.7376085 241.79 32.39 ..."
                        fill="#EB001B"
                    />
                </G>
            </G>
        </Svg>
    );

    const AmexIcon = () => (
        <Svg width={50} height={50} viewBox="0 0 750 471">
            <G fill="none" fillRule="evenodd">
                <Rect fill="#2557D6" width="750" height="471" rx="40" />
                <Path
                    fill="#FFFFFF"
                    d="M554.594 130.608l-14.521 35.039h29.121L554.594 130.608z M387.03 152.321c2.738-1.422 4.349-4.515 ..."
                />
            </G>
        </Svg>
    );

    return (
        <View
            style={{
                marginTop: spV(28),
            }}
        >
            <View style={{ position: "relative", marginBottom: "20px", width: "-webkit-fill-available" }}>
                <Input
                    inputContainerStyle={{
                        borderWidth: 1,
                        borderColor: errorMessage?.cardNumber ? "#E20E0E" : "#21242799",
                        borderRadius: 4,
                        color: errorMessage?.cardNumber ? "#E20E0E" : "#21242799",
                    }}
                    containerStyle={{
                        width: wp(450),
                        backgroundColor: "white",
                    }}
                    textInputProps={{
                        returnKeyType: "next",
                    }}
                    placeholder="xxxx xxxx xxxx xxxx"
                    propStyle={{}}
                    onChangeText={(text) => {
                        const cleanedNumber = text.replace(/\D+/g, ""); // Remove non-numeric characters
                        const detectedCard = detectCardType(cleanedNumber);
                        const maskedCardNumber = applyMask(cleanedNumber, detectedCard.mask);
                        setSelectedCard(null);
                        setCcType(detectedCard.cardType);
                        setCardNumber(maskedCardNumber);
                    }}
                    value={cardNumber}
                />
                <VText style={Styles.label}> Card Number </VText>
                {errorMessage?.cardNumber && (
                    <VText style={{ color: "#E20E0E", fontSize: fp(13), fontFamily: "JosefinSans", marginTop: spV(5) }}>
                        {errorMessage?.cardNumber}
                    </VText>
                )}
            </View>

            <View style={{ position: "relative", marginBottom: "20px", width: "-webkit-fill-available" }}>
                <Input
                    inputContainerStyle={{
                        borderWidth: 1,
                        borderColor: "#21242799",
                        borderRadius: 4,
                        color: "#21242799",
                    }}
                    containerStyle={{
                        width: wp(450),
                        backgroundColor: "white",
                    }}
                    textInputProps={{
                        returnKeyType: "next",
                    }}
                    placeholder="Name on Card"
                    propStyle={{}}
                    onChangeText={(e) => {
                        setName(e);
                        setSelectedCard(null);
                    }}
                    value={name}
                />
                <VText style={Styles.label}> Name on Card </VText>
            </View>

            <View
                style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    gap: spH(10),
                }}
            >
                <View
                    style={{
                        position: "relative",
                        marginBottom: "20px",
                        width: "-webkit-fill-available",
                        width: spH(220),
                    }}
                >
                    <Input
                        containerStyle={{
                            width: spH(220),
                            backgroundColor: "white",
                        }}
                        inputContainerStyle={{
                            borderWidth: 1,
                            borderColor: errorMessage?.expiryDate ? "#E20E0E" : "#21242799",
                            borderRadius: 4,
                            color: errorMessage?.expiryDate ? "#E20E0E" : "#21242799",
                        }}
                        textInputProps={{
                            returnKeyType: "next",
                        }}
                        placeholder="MM/YY"
                        propStyle={{}}
                        onChangeText={(text) => {
                            const cleaned = text.replace(/\D+/g, ""); // Remove non-numeric characters
                            const month = cleaned.substring(0, 2);
                            const year = cleaned.substring(2, 4);
                            let formattedExpiryDate = month;
                            if (cleaned.length > 2) {
                                formattedExpiryDate = `${month}/${year}`;
                            }
                            setExpirationDate(formattedExpiryDate);
                            setSelectedCard(null);
                        }}
                        value={expirationDate}
                    />
                    <VText style={Styles.label}> Valid Thru </VText>
                    {errorMessage?.expiryDate && (
                        <VText
                            style={{ color: "#E20E0E", fontSize: fp(13), fontFamily: "JosefinSans", marginTop: spV(5) }}
                        >
                            {errorMessage?.expiryDate}
                        </VText>
                    )}
                </View>
                <View
                    style={{
                        position: "relative",
                        marginBottom: "20px",
                        width: "-webkit-fill-available",
                        width: spH(220),
                    }}
                >
                    <Input
                        containerStyle={{
                            width: spH(220),
                            backgroundColor: "white",
                        }}
                        inputContainerStyle={{
                            borderWidth: 1,
                            borderColor: errorMessage?.securityCode ? "#E20E0E" : "#21242799",
                            borderRadius: 4,
                            color: errorMessage?.securityCode ? "#E20E0E" : "#21242799",
                        }}
                        textInputProps={{
                            returnKeyType: "next",
                        }}
                        maxLength={4}
                        placeholder="XXX"
                        propStyle={{}}
                        onChangeText={(text) => {
                            const cleaned = text.replace(/\D+/g, ""); // Remove non-numeric characters
                            setSecurityCode(cleaned);
                            setSelectedCard(null);
                        }}
                        value={securityCode}
                    />
                    <VText style={Styles.label}> Security Code </VText>
                    {errorMessage?.securityCode && (
                        <VText
                            style={{ color: "#E20E0E", fontSize: fp(13), fontFamily: "JosefinSans", marginTop: spV(5) }}
                        >
                            {errorMessage?.securityCode}
                        </VText>
                    )}
                </View>
            </View>

            <View
                style={{ position: "relative", marginBottom: "20px", width: "-webkit-fill-available", width: spH(220) }}
            >
                <Input
                    containerStyle={{
                        width: spH(450),
                        backgroundColor: "white",
                    }}
                    inputContainerStyle={{
                        borderWidth: 1,
                        borderColor: errorMessage?.zipCode ? "#E20E0E" : "#21242799",
                        borderRadius: 4,
                        color: errorMessage?.zipCode ? "#E20E0E" : "#21242799",
                    }}
                    textInputProps={{
                        returnKeyType: "next",
                    }}
                    maxLength={5}
                    placeholder="xxxxxx"
                    propStyle={{}}
                    onChangeText={(text) => {
                        const cleaned = text.replace(/\D+/g, ""); // Remove non-numeric characters
                        setZipCode(cleaned);
                        setSelectedCard(null);
                    }}
                    value={zipCode}
                />
                <VText style={Styles.label}> Zip Code </VText>
                {errorMessage?.zipCode && (
                    <VText style={{ color: "#E20E0E", fontSize: fp(13), fontFamily: "JosefinSans", marginTop: spV(5) }}>
                        {errorMessage?.zipCode}
                    </VText>
                )}
            </View>

            {userProfile?.userId && !userProfile?.isGuest ? (
                <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 15 }}>
                    <TouchableOpacity
                        onPress={() => setSaveCardChecked(!saveCardChecked)}
                        style={{ marginRight: 10, borderWidth: 1, borderColor: "#21242799", borderRadius: 4 }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {saveCardChecked && (
                                <>
                                    <rect width="24" height="24" rx="2.66667" fill="black" />
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M17.3346 8.80118L9.97388 16L6.66797 12.7668L7.48717 11.9656L9.97388 14.3976L16.5154 8L17.3346 8.80118Z"
                                        fill="white"
                                    />
                                </>
                            )}
                        </svg>
                    </TouchableOpacity>

                    <VText
                        onPress={() => setSaveCardChecked(!saveCardChecked)}
                        style={{ fontSize: spH(16), color: "#21242799", fontWeight: "350", marginTop: 5 }}
                    >
                        Save this card securely for future purchases
                    </VText>
                </View>
            ) : (
                ""
            )}

            <Buttons
                disabled={(!cardNumber || !name || !expirationDate || !securityCode || !zipCode) && !selectedCard}
                buttonContainerStyle={{
                    fontWeight: "700",
                }}
                containerStyle={{
                    width: wp(312),
                    marginTop: spV(28),
                    height: spV(56),
                    opacity:
                        (!cardNumber || !name || !expirationDate || !securityCode || !zipCode) && !selectedCard
                            ? 0.5
                            : 1,
                }}
                loading={loader}
                onPress={handlePayment}
                text="Pay Now"
            />
            <Loader loading={loader} />
        </View>
    );
};

const Styles = StyleSheet.create({
    label: {
        position: "absolute",
        paddingLeft: 10,
        paddingRight: 10,
        top: "-6px",
        left: "15px",
        fontFamily: "JosefinSans",
        fontSize: fp(16),
        color: "#999",
        fontWeight: "350",
        transition: "0.2s ease all",
        pointerEvents: "none",
        backgroundColor: "white",
    },
    input: {
        width: wp(450),
        backgroundColor: "white",
    },
});

export default Payment;
